import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchBrowse } from '@/api/discovery'
import type { BrowsePage, BrowseSort } from '@/api/types'

interface UseBrowseInfiniteOptions {
  q?: string
  sort?: BrowseSort
  size?: number
  /** Phase 19: optional community filter (slug). */
  community?: string
}

/**
 * Infinite-scroll variant of {@code useBrowse}. Hits the same `/browse` endpoint one page at
 * a time and returns a list of {@link BrowsePage} pages — call-site flattens
 * {@code pages.flatMap(p => p.items)} for rendering. {@code fetchNextPage()} loads the next
 * page; {@code hasNextPage} reflects whether the server reported more.
 *
 * <p>Stale-time mirrors {@code useBrowse} — kept under the 1-hour signed-hero-URL TTL so a
 * long browse session doesn't end with broken thumbs.
 */
export const useBrowseInfinite = ({
  q,
  sort = 'recent',
  size = 20,
  community,
}: UseBrowseInfiniteOptions) =>
  useInfiniteQuery<BrowsePage, Error, BrowsePage[]>({
    queryKey: ['browse', 'infinite', { q, sort, size, community }],
    queryFn: ({ pageParam }) =>
      fetchBrowse({ q, sort, page: pageParam as number, size, community }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const next = lastPage.page + 1
      return next < lastPage.totalPages ? next : undefined
    },
    // The Pages container's selector flattens the React Query `pages` array into a plain list
    // of pages — call sites don't need to know about the `pageParams` carrier.
    select: (data) => data.pages,
    staleTime: 50 * 60 * 1000,
  })
